import React, { useEffect, useMemo, useState } from 'react';
import RialSymbol from '../components/RialSymbol';
import axios from '../api';
import { useAuth } from '../contexts/AuthContext';
import customerService from '../services/customer.service';
import merchantService from '../services/merchant.service';
import PaginationControls from '../components/PaginationControls';
import {
  Box, Button, Card, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, FormControlLabel, InputAdornment, InputLabel, MenuItem,
  Select, SelectChangeEvent, Switch, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography,
  Alert, Snackbar, IconButton, Stack,
} from '@mui/material';
import { Block, CheckCircle, Search, Add, Close, Edit, Delete, Favorite, FavoriteBorder, ShoppingCart, Remove, AddCircle } from '@mui/icons-material';

type BoxStatus = 'Active' | 'Sold out' | 'Expired' | 'Disabled';

interface SurpriseBox {
  id: string;
  name: string;
  merchant: string;
  businessId: string;
  price: number;
  originalPrice: number;
  quantity: number;
  sold: number;
  pickup: string;
  pickupStart: string;
  pickupEnd: string;
  pickupDate: string;
  status: BoxStatus;
  category: string;
  description: string;
  images: string[];
  dietaryInfo?: {
    vegetarian?: boolean;
    vegan?: boolean;
    glutenFree?: boolean;
    halal?: boolean;
  };
}

const statusColor: Record<BoxStatus, 'success' | 'warning' | 'default' | 'error'> = {
  Active: 'success', 'Sold out': 'warning', Expired: 'default', Disabled: 'error',
};

const categories = ['Bakery', 'Cafe', 'Restaurant', 'Desserts', 'Breakfast', 'Meals'];

const normalizeBox = (item: any): SurpriseBox => ({
  id: String(item._id || item.id),
  name: item.name || 'Unnamed Box',
  merchant: item.business?.name || item.merchant || 'Unassigned merchant',
  businessId: String(item.business?._id || item.business || ''),
  price: item.pricing?.discountedPrice ?? item.price ?? 0,
  originalPrice: item.pricing?.originalPrice ?? item.originalPrice ?? 0,
  quantity: item.inventory?.totalBoxes ?? item.quantity ?? 0,
  sold: item.inventory ? (item.inventory.totalBoxes ?? 0) - (item.inventory.remainingBoxes ?? 0) : item.sold ?? 0,
  pickup: item.pickupTime ? `${item.pickupTime.start} - ${item.pickupTime.end}` : item.pickup ?? 'Not set',
  pickupStart: item.pickupTime?.start || '',
  pickupEnd: item.pickupTime?.end || '',
  pickupDate: item.pickupTime?.date
    ? new Date(item.pickupTime.date).toISOString().split('T')[0]
    : new Date().toISOString().split('T')[0],
  status: item.status === 'sold_out' || (item.inventory && item.inventory.remainingBoxes <= 0 && item.inventory.totalBoxes > 0) ? 'Sold out' : item.status === 'expired' ? 'Expired' : item.status === 'cancelled' || item.status === 'disabled' ? 'Disabled' : 'Active',
  category: item.category || 'Meals',
  description: item.description || '',
  images: item.images || [],
  dietaryInfo: item.dietaryInfo || {},
});

export default function SurpriseBoxes() {
  const { user } = useAuth();
  const isCustomer = user?.role === 'customer';
  const isMerchant = user?.role === 'merchant';

  const [boxes, setBoxes] = useState<SurpriseBox[]>([]);
  const [businesses, setBusinesses] = useState<any[]>([]);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState(isCustomer ? 'Active' : 'All');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingBox, setEditingBox] = useState<SurpriseBox | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' | 'warning' | 'info' });
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const [cart, setCart] = useState<any>(null);
  const [cartItems, setCartItems] = useState<Map<string, number>>(new Map());

  // Form state
  const [formData, setFormData] = useState({
    businessId: '',
    name: '',
    description: '',
    category: 'Meals',
    originalPrice: '',
    discountedPrice: '',
    totalBoxes: '',
    pickupStart: '',
    pickupEnd: '',
    pickupDate: new Date().toISOString().split('T')[0],
    imageUrl: '',
    vegetarian: false,
    vegan: false,
    glutenFree: false,
    halal: false,
  });

  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  useEffect(() => {
    loadData();
  }, [page, rowsPerPage]);

  useEffect(() => {
    if (isCustomer) {
      loadFavorites();
      loadCart();
    }
  }, [isCustomer]);

  const loadData = async () => {
    try {
      if (isMerchant) {
        // Merchant sees only their own boxes
        const boxesRes = await merchantService.getSurpriseBoxes({ page, limit: rowsPerPage });
        const businessRes = await merchantService.getBusiness();

        const boxesArray = boxesRes.surpriseBoxes || [];
        setBoxes(Array.isArray(boxesArray) ? boxesArray.map(normalizeBox) : []);
        setTotal(boxesRes.total ?? boxesArray.length);
        setBusinesses([businessRes]); // Merchant only sees their own business
      } else if (isCustomer) {
        // Customers browse the shared catalogue of active boxes. This endpoint is
        // available to any authenticated role, unlike the admin-only /api/admin/*
        // routes (which return 403 for customers and caused "Unable to load
        // surprise boxes").
        const res = await axios.get('/api/surprise-boxes', { params: { status: 'active' } });
        const boxesArray = Array.isArray(res.data)
          ? res.data
          : res.data.boxes || res.data.surpriseBoxes || [];
        setBoxes(Array.isArray(boxesArray) ? boxesArray.map(normalizeBox) : []);
        setTotal(boxesArray.length);
        setBusinesses([]); // customers don't filter by business
      } else {
        // Admin sees all boxes
        const [boxesRes, businessesRes] = await Promise.all([
          axios.get('/api/admin/surprise-boxes', { params: { page, limit: rowsPerPage } }),
          axios.get('/api/admin/businesses'),
        ]);

        const boxesArray = boxesRes.data.boxes || boxesRes.data;
        setBoxes(Array.isArray(boxesArray) ? boxesArray.map(normalizeBox) : []);
        setTotal(boxesRes.data.total ?? boxesArray.length);

        const businessesArray = businessesRes.data.businesses || businessesRes.data;
        setBusinesses(Array.isArray(businessesArray) ? businessesArray : []);
      }

      setError(false);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const loadFavorites = async () => {
    try {
      const favs = await customerService.getFavorites();
      const favIds = favs.map((fav: any) => fav._id || fav);
      setFavorites(new Set(favIds));
    } catch (err) {
      console.error('Error loading favorites:', err);
    }
  };

  const loadCart = async () => {
    try {
      const cartData = await customerService.getCart();
      setCart(cartData);

      // Build map of boxId -> quantity
      const itemsMap = new Map<string, number>();
      if (cartData && cartData.items) {
        cartData.items.forEach((item: any) => {
          const boxId = item.surpriseBox?._id || item.surpriseBox;
          if (boxId) {
            itemsMap.set(String(boxId), item.quantity);
          }
        });
      }
      setCartItems(itemsMap);
    } catch (err) {
      console.error('Error loading cart:', err);
    }
  };

  const handleResetInventory = async (box: SurpriseBox) => {
    try {
      await merchantService.resetInventory(box.id, box.quantity);
      setBoxes((current) => current.map((item) => item.id === box.id
        ? { ...item, sold: 0, status: 'Active' as BoxStatus } : item));
      setSnackbar({ open: true, message: 'Inventory reset successfully!', severity: 'success' });
    } catch (err) {
      console.error('Error resetting inventory:', err);
      setSnackbar({ open: true, message: 'Failed to reset inventory', severity: 'error' });
    }
  };

  const filteredBoxes = useMemo(() => boxes.filter((item) => {
    const matchesQuery = `${item.name} ${item.merchant} ${item.id}`.toLowerCase().includes(query.toLowerCase());
    return matchesQuery && (statusFilter === 'All' || item.status === statusFilter);
  }), [boxes, query, statusFilter]);

  const handleOpenDialog = (box?: SurpriseBox) => {
    if (box) {
      setEditingBox(box);
      setFormData({
        businessId: box.businessId,
        name: box.name,
        description: box.description,
        category: box.category,
        originalPrice: box.originalPrice.toString(),
        discountedPrice: box.price.toString(),
        totalBoxes: box.quantity.toString(),
        pickupStart: box.pickupStart,
        pickupEnd: box.pickupEnd,
        pickupDate: box.pickupDate,
        imageUrl: box.images[0] || '',
        vegetarian: box.dietaryInfo?.vegetarian || false,
        vegan: box.dietaryInfo?.vegan || false,
        glutenFree: box.dietaryInfo?.glutenFree || false,
        halal: box.dietaryInfo?.halal || false,
      });
      setImagePreview(box.images[0] || null);
    } else {
      setEditingBox(null);
      // For merchants, auto-set businessId
      const merchantBusinessId = isMerchant && businesses.length > 0 ? (businesses[0]._id || businesses[0].id) : '';
      setFormData({
        businessId: merchantBusinessId,
        name: '',
        description: '',
        category: 'Meals',
        originalPrice: '',
        discountedPrice: '',
        totalBoxes: '',
        pickupStart: '17:00',
        pickupEnd: '19:00',
        pickupDate: new Date().toISOString().split('T')[0],
        imageUrl: '',
        vegetarian: false,
        vegan: false,
        glutenFree: false,
        halal: false,
      });
      setImagePreview(null);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingBox(null);
    setImagePreview(null);
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setSnackbar({ open: true, message: 'Please select an image file', severity: 'error' });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setSnackbar({ open: true, message: 'Image size must be less than 5MB', severity: 'error' });
      return;
    }

    setUploadingImage(true);

    try {
      // Create FormData for image upload
      const formData = new FormData();
      formData.append('image', file);

      // Upload to backend
      const response = await axios.post('/api/upload/image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const imageUrl = response.data.url || response.data.imageUrl;

      // Update form data with uploaded image URL
      setFormData(prev => ({ ...prev, imageUrl }));
      setImagePreview(imageUrl);
      setSnackbar({ open: true, message: 'Image uploaded successfully!', severity: 'success' });
    } catch (err: any) {
      console.error('Error uploading image:', err);

      // Fallback: Use FileReader to create data URL for preview
      const reader = new FileReader();
      reader.onloadend = () => {
        const dataUrl = reader.result as string;
        setImagePreview(dataUrl);
        setFormData(prev => ({ ...prev, imageUrl: dataUrl }));
        setSnackbar({
          open: true,
          message: 'Using local preview. Image will be saved as base64.',
          severity: 'warning'
        });
      };
      reader.readAsDataURL(file);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleRemoveImage = () => {
    setImagePreview(null);
    setFormData(prev => ({ ...prev, imageUrl: '' }));
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        business: formData.businessId,
        name: formData.name,
        description: formData.description,
        category: formData.category,
        images: formData.imageUrl ? [formData.imageUrl] : ['https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=800'],
        pricing: {
          originalPrice: parseFloat(formData.originalPrice),
          discountedPrice: parseFloat(formData.discountedPrice),
        },
        inventory: {
          totalBoxes: parseInt(formData.totalBoxes),
          remainingBoxes: editingBox
            ? Math.max(0, parseInt(formData.totalBoxes) - editingBox.sold)
            : parseInt(formData.totalBoxes),
        },
        pickupTime: {
          start: formData.pickupStart,
          end: formData.pickupEnd,
          date: new Date(formData.pickupDate),
        },
        dietary: formData.vegetarian || formData.vegan || formData.glutenFree || formData.halal ? [
          ...(formData.vegetarian ? ['vegetarian'] : []),
          ...(formData.vegan ? ['vegan'] : []),
          ...(formData.glutenFree ? ['gluten-free'] : []),
          ...(formData.halal ? ['halal'] : []),
        ] : [],
        status: 'active',
      };

      if (editingBox) {
        if (isMerchant) {
          await merchantService.updateSurpriseBox(editingBox.id, payload);
        } else {
          await axios.put(`/api/surprise-boxes/${editingBox.id}`, payload);
        }
        setSnackbar({ open: true, message: 'Surprise box updated successfully!', severity: 'success' });
      } else {
        if (isMerchant) {
          await merchantService.createSurpriseBox(payload);
        } else {
          await axios.post('/api/surprise-boxes', payload);
        }
        setSnackbar({ open: true, message: 'Surprise box created successfully!', severity: 'success' });
      }

      handleCloseDialog();
      loadData();
    } catch (err: any) {
      console.error('Error saving surprise box:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.error?.message || err.response?.data?.error || 'Failed to save surprise box',
        severity: 'error'
      });
    }
  };

  const toggleStatus = async (id: string) => {
    try {
      const box = boxes.find(b => b.id === id);
      if (!box) return;

      if (isMerchant) {
        // Merchant can only mark as sold out
        await merchantService.markAsSoldOut(id);
        setBoxes((current) => current.map((item) => item.id === id
          ? { ...item, status: 'Sold out' as BoxStatus } : item));
        setSnackbar({ open: true, message: 'Marked as sold out!', severity: 'success' });
      } else {
        // Admin can toggle status
        const newStatus = box.status === 'Disabled' ? 'active' : 'cancelled';
        await axios.patch(`/api/surprise-boxes/${id}`, { status: newStatus });

        setBoxes((current) => current.map((item) => item.id === id
          ? { ...item, status: item.status === 'Disabled' ? 'Active' : 'Disabled' } : item));

        setSnackbar({ open: true, message: 'Status updated successfully!', severity: 'success' });
      }
    } catch (err) {
      console.error('Error updating status:', err);
      setSnackbar({ open: true, message: 'Failed to update status', severity: 'error' });
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this surprise box?')) return;

    try {
      if (isMerchant) {
        await merchantService.deleteSurpriseBox(id);
      } else {
        await axios.delete(`/api/surprise-boxes/${id}`);
      }
      setSnackbar({ open: true, message: 'Surprise box deleted successfully!', severity: 'success' });
      loadData();
    } catch (err: any) {
      console.error('Error deleting surprise box:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'Failed to delete surprise box',
        severity: 'error'
      });
    }
  };

  const toggleFavorite = async (id: string) => {
    try {
      const isFavorite = favorites.has(id);

      if (isFavorite) {
        await customerService.removeFromFavorites(id);
        setFavorites((current) => {
          const newFavorites = new Set(current);
          newFavorites.delete(id);
          return newFavorites;
        });
        setSnackbar({ open: true, message: 'Removed from favorites', severity: 'info' });
      } else {
        await customerService.addToFavorites(id);
        setFavorites((current) => {
          const newFavorites = new Set(current);
          newFavorites.add(id);
          return newFavorites;
        });
        setSnackbar({ open: true, message: 'Added to favorites!', severity: 'success' });
      }
    } catch (err: any) {
      console.error('Error toggling favorite:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.error?.message || 'Failed to update favorites',
        severity: 'error'
      });
    }
  };

  const handleAddToCart = async (box: SurpriseBox) => {
    // Check inventory availability
    const availableStock = box.quantity - box.sold;
    const currentInCart = cartItems.get(box.id) || 0;

    if (currentInCart >= availableStock) {
      setSnackbar({
        open: true,
        message: `Only ${availableStock} available in stock`,
        severity: 'warning'
      });
      return;
    }

    // Optimistic update - update UI immediately
    const newQty = currentInCart + 1;
    setCartItems((prev) => {
      const newMap = new Map(prev);
      newMap.set(box.id, newQty);
      return newMap;
    });
    setSnackbar({ open: true, message: `Added ${box.name} to cart!`, severity: 'success' });

    // Then sync with backend in background
    try {
      await customerService.addToCart(box.id, 1);
    } catch (err: any) {
      console.error('Error adding to cart:', err);
      // Revert on error
      setCartItems((prev) => {
        const newMap = new Map(prev);
        newMap.set(box.id, currentInCart);
        return newMap;
      });
      const errorMessage = err.response?.data?.error?.message
        || err.response?.data?.message
        || err.message
        || 'Failed to add to cart';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    }
  };

  const handleUpdateCartQuantity = async (box: SurpriseBox, change: number) => {
    const currentQty = cartItems.get(box.id) || 0;
    const newQty = currentQty + change;
    const availableStock = box.quantity - box.sold;

    if (newQty <= 0) {
      // Optimistic update - remove immediately from UI
      setCartItems((prev) => {
        const newMap = new Map(prev);
        newMap.delete(box.id);
        return newMap;
      });
      setSnackbar({ open: true, message: 'Removed from cart', severity: 'info' });

      // Sync with backend
      try {
        const cartData = cart?.items?.find((item: any) =>
          String(item.surpriseBox?._id || item.surpriseBox) === box.id
        );
        if (cartData?._id) {
          await customerService.removeFromCart(cartData._id);
        }
      } catch (err: any) {
        console.error('Error removing from cart:', err);
        // Revert on error
        setCartItems((prev) => {
          const newMap = new Map(prev);
          newMap.set(box.id, currentQty);
          return newMap;
        });
        setSnackbar({ open: true, message: 'Failed to update cart', severity: 'error' });
      }
    } else if (newQty > availableStock) {
      // Exceeds inventory
      setSnackbar({
        open: true,
        message: `Only ${availableStock} available in stock`,
        severity: 'warning'
      });
    } else {
      // Optimistic update - update UI immediately
      setCartItems((prev) => {
        const newMap = new Map(prev);
        newMap.set(box.id, newQty);
        return newMap;
      });
      setSnackbar({ open: true, message: 'Cart updated', severity: 'success' });

      // Sync with backend
      try {
        const cartData = cart?.items?.find((item: any) =>
          String(item.surpriseBox?._id || item.surpriseBox) === box.id
        );
        if (cartData?._id) {
          await customerService.updateCartItem(cartData._id, newQty);
        }
      } catch (err: any) {
        console.error('Error updating cart:', err);
        // Revert on error
        setCartItems((prev) => {
          const newMap = new Map(prev);
          newMap.set(box.id, currentQty);
          return newMap;
        });
        setSnackbar({ open: true, message: 'Failed to update cart', severity: 'error' });
      }
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {isCustomer ? 'Browse Surprise Boxes' : 'Surprise Boxes'}
          </Typography>
          <Typography color="text.secondary">
            {isCustomer
              ? 'Discover amazing deals and help reduce food waste'
              : 'Review listings, inventory, and pickup windows.'}
          </Typography>
        </Box>
        {!isCustomer && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
          >
            Add Box
          </Button>
        )}
      </Box>

      <Card>
        <Box sx={{ display: 'flex', gap: 2, p: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            label="Search boxes"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            sx={{ minWidth: { xs: '100%', sm: 300 } }}
            slotProps={{
              input: {
                startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>
              }
            }}
          />
          {!isCustomer && (
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel id="box-status-label">Status</InputLabel>
              <Select
                labelId="box-status-label"
                value={statusFilter}
                label="Status"
                onChange={(event: SelectChangeEvent) => setStatusFilter(event.target.value)}
              >
                {['All', 'Active', 'Sold out', 'Expired', 'Disabled'].map((option) =>
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                )}
              </Select>
            </FormControl>
          )}
        </Box>

        {error && (
          <Typography color="error" sx={{ p: 3 }}>
            Unable to load surprise boxes. Check that the backend and MongoDB are running.
          </Typography>
        )}

        {!error && (
          <TableContainer>
            <Table sx={{ minWidth: 980 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Box</TableCell>
                  <TableCell>Merchant</TableCell>
                  <TableCell>Price</TableCell>
                  <TableCell>Inventory</TableCell>
                  <TableCell>Pickup window</TableCell>
                  {!isCustomer && <TableCell>Status</TableCell>}
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && (
                  <TableRow>
                    <TableCell colSpan={isCustomer ? 6 : 7} align="center">
                      <Typography sx={{ py: 5 }}>Loading surprise boxes...</Typography>
                    </TableCell>
                  </TableRow>
                )}

                {!loading && filteredBoxes.map((item) => {
                  const discount = Math.round((1 - item.price / item.originalPrice) * 100);
                  return (
                    <TableRow key={item.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box
                            component="img"
                            src={item.images[0] || 'https://via.placeholder.com/80x80?text=No+Image'}
                            alt={item.name}
                            sx={{
                              width: 60,
                              height: 60,
                              objectFit: 'cover',
                              borderRadius: 1,
                              border: '1px solid #e0e0e0',
                            }}
                          />
                          <Box>
                            <Typography sx={{ fontWeight: 600 }}>{item.name}</Typography>
                            <Typography variant="caption" color="text.secondary">{item.category}</Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>{item.merchant}</TableCell>
                      <TableCell>
                        <Typography sx={{ fontWeight: 600 }}><RialSymbol /> {item.price.toFixed(2)}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                          <RialSymbol /> {item.originalPrice.toFixed(2)} · {discount}% off
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {item.sold} sold <Typography component="span" color="text.secondary">/ {item.quantity}</Typography>
                      </TableCell>
                      <TableCell>{item.pickup}</TableCell>
                      {!isCustomer && (
                        <TableCell>
                          <Chip label={item.status} color={statusColor[item.status]} size="small" />
                        </TableCell>
                      )}
                      <TableCell align="right">
                        {isCustomer ? (
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', alignItems: 'center' }}>
                            <IconButton
                              size="small"
                              color={favorites.has(item.id) ? 'error' : 'default'}
                              onClick={() => toggleFavorite(item.id)}
                              title={favorites.has(item.id) ? 'Remove from favorites' : 'Add to favorites'}
                            >
                              {favorites.has(item.id) ? <Favorite fontSize="small" /> : <FavoriteBorder fontSize="small" />}
                            </IconButton>
                            {cartItems.has(item.id) ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'primary.light', borderRadius: 2, px: 1, py: 0.5 }}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleUpdateCartQuantity(item, -1)}
                                  sx={{ color: 'white' }}
                                >
                                  <Remove fontSize="small" />
                                </IconButton>
                                <Typography sx={{ minWidth: 30, textAlign: 'center', fontWeight: 600, color: 'white' }}>
                                  {cartItems.get(item.id)}
                                </Typography>
                                <IconButton
                                  size="small"
                                  onClick={() => handleUpdateCartQuantity(item, 1)}
                                  sx={{ color: 'white' }}
                                  disabled={item.status !== 'Active' || (cartItems.get(item.id) || 0) >= (item.quantity - item.sold)}
                                  title={(cartItems.get(item.id) || 0) >= (item.quantity - item.sold) ? 'Maximum stock reached' : 'Add one more'}
                                >
                                  <AddCircle fontSize="small" />
                                </IconButton>
                              </Box>
                            ) : (
                              <Button
                                size="small"
                                variant="contained"
                                startIcon={<ShoppingCart />}
                                onClick={() => handleAddToCart(item)}
                                disabled={item.status !== 'Active'}
                                sx={{ minWidth: 120 }}
                              >
                                Add to Cart
                              </Button>
                            )}
                          </Box>
                        ) : (
                          <>
                            <IconButton size="small" color="primary" onClick={() => handleOpenDialog(item)}>
                              <Edit fontSize="small" />
                            </IconButton>
                            <IconButton size="small" color="error" onClick={() => handleDelete(item.id)}>
                              <Delete fontSize="small" />
                            </IconButton>
                            {isMerchant ? (
                              <>
                                <Button size="small" startIcon={<Block />} onClick={() => toggleStatus(item.id)}>
                                  Sold Out
                                </Button>
                                <Button size="small" color="success" startIcon={<CheckCircle />} onClick={() => handleResetInventory(item)}>
                                  Reset
                                </Button>
                              </>
                            ) : (
                              <Button
                                size="small"
                                startIcon={item.status === 'Disabled' ? <CheckCircle /> : <Block />}
                                onClick={() => toggleStatus(item.id)}
                              >
                                {item.status === 'Disabled' ? 'Enable' : 'Disable'}
                              </Button>
                            )}
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}

                {!loading && filteredBoxes.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={isCustomer ? 6 : 7} align="center">
                      <Typography sx={{ py: 5 }} color="text.secondary">
                        No surprise boxes match these filters.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      <PaginationControls
        page={page}
        total={total}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={(value) => { setRowsPerPage(value); setPage(1); }}
      />

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingBox ? 'Edit Surprise Box' : 'Add New Surprise Box'}
          <IconButton
            onClick={handleCloseDialog}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {!isMerchant && (
              <FormControl fullWidth>
                <InputLabel>Business *</InputLabel>
                <Select
                  value={formData.businessId}
                  label="Business *"
                  onChange={(e) => setFormData({ ...formData, businessId: e.target.value })}
                >
                  {businesses.map((business) => (
                    <MenuItem key={business._id || business.id} value={business._id || business.id}>
                      {business.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {isMerchant && businesses.length > 0 && (
              <Alert severity="info">
                Creating surprise box for: <strong>{businesses[0]?.name}</strong>
              </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Box Name *"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
              <FormControl fullWidth>
                <InputLabel>Category *</InputLabel>
                <Select
                  value={formData.category}
                  label="Category *"
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                >
                  {categories.map((cat) => (
                    <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Description *"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                type="number"
                label="Original Price (OMR) *"
                value={formData.originalPrice}
                onChange={(e) => setFormData({ ...formData, originalPrice: e.target.value })}
                slotProps={{ htmlInput: { step: '0.01', min: '0' } }}
              />
              <TextField
                fullWidth
                type="number"
                label="Discounted Price (OMR) *"
                value={formData.discountedPrice}
                onChange={(e) => setFormData({ ...formData, discountedPrice: e.target.value })}
                slotProps={{ htmlInput: { step: '0.01', min: '0' } }}
              />
              <TextField
                fullWidth
                type="number"
                label="Total Boxes *"
                value={formData.totalBoxes}
                onChange={(e) => setFormData({ ...formData, totalBoxes: e.target.value })}
                slotProps={{ htmlInput: { min: '1' } }}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                type="time"
                label="Pickup Start *"
                value={formData.pickupStart}
                onChange={(e) => setFormData({ ...formData, pickupStart: e.target.value })}
                slotProps={{ inputLabel: { shrink: true } }}
              />
              <TextField
                fullWidth
                type="time"
                label="Pickup End *"
                value={formData.pickupEnd}
                onChange={(e) => setFormData({ ...formData, pickupEnd: e.target.value })}
                slotProps={{ inputLabel: { shrink: true } }}
              />
              <TextField
                fullWidth
                type="date"
                label="Pickup Date *"
                value={formData.pickupDate}
                onChange={(e) => setFormData({ ...formData, pickupDate: e.target.value })}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Box Image</Typography>

              {imagePreview ? (
                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                  <Box
                    component="img"
                    src={imagePreview}
                    alt="Preview"
                    sx={{
                      width: '100%',
                      maxWidth: 400,
                      height: 200,
                      objectFit: 'cover',
                      borderRadius: 2,
                      border: '1px solid #ddd',
                    }}
                  />
                  <IconButton
                    onClick={handleRemoveImage}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      bgcolor: 'rgba(255, 255, 255, 0.9)',
                      '&:hover': { bgcolor: 'rgba(255, 255, 255, 1)' },
                    }}
                    size="small"
                  >
                    <Close />
                  </IconButton>
                </Box>
              ) : (
                <Box
                  sx={{
                    width: '100%',
                    maxWidth: 400,
                    height: 200,
                    border: '2px dashed #ccc',
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    '&:hover': {
                      borderColor: 'primary.main',
                      bgcolor: 'rgba(11, 110, 79, 0.04)',
                    },
                  }}
                  onClick={() => document.getElementById('image-upload')?.click()}
                >
                  <Add sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Click to upload image
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    PNG, JPG up to 5MB
                  </Typography>
                </Box>
              )}

              <input
                id="image-upload"
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                style={{ display: 'none' }}
              />

              {uploadingImage && (
                <Typography variant="caption" color="primary" sx={{ mt: 1, display: 'block' }}>
                  Uploading image...
                </Typography>
              )}
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Dietary Information</Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.vegetarian}
                      onChange={(e) => setFormData({ ...formData, vegetarian: e.target.checked })}
                    />
                  }
                  label="Vegetarian"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.vegan}
                      onChange={(e) => setFormData({ ...formData, vegan: e.target.checked })}
                    />
                  }
                  label="Vegan"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.glutenFree}
                      onChange={(e) => setFormData({ ...formData, glutenFree: e.target.checked })}
                    />
                  }
                  label="Gluten Free"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.halal}
                      onChange={(e) => setFormData({ ...formData, halal: e.target.checked })}
                    />
                  }
                  label="Halal"
                />
              </Box>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={
              !formData.businessId ||
              !formData.name ||
              !formData.description ||
              !formData.originalPrice ||
              !formData.discountedPrice ||
              !formData.totalBoxes ||
              !formData.pickupStart ||
              !formData.pickupEnd
            }
          >
            {editingBox ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
